import { Cookies } from 'react-cookie';
import { Logger } from '@saviynt/common';
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from 'axios';

import { AXIOS_TIMEOUT_ERROR_MESSAGE } from '../constants';
import { checkEnvironment } from '../utils';

const cookies = new Cookies();

// Guards `window.location.reload()` so a persistent 401 cannot cause an
// infinite reload loop (e.g. an endpoint the user is not provisioned for in a
// given environment). We reload at most once per browser session; a successful
// response clears the flag so a genuine future session-expiry can reload again.
const RELOAD_GUARD_KEY = 'aag.authReloadInProgress';

const hasReloadedForAuth = (): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === 'true';
  } catch {
    // sessionStorage may be unavailable (privacy mode); fail safe by treating
    // it as "already reloaded" so we never loop.
    return true;
  }
};

const markReloadedForAuth = (): void => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, 'true');
  } catch {
    // Ignore storage failures; the read-side fail-safe prevents looping.
  }
};

const clearReloadGuard = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // Ignore storage failures.
  }
};

/**
 * Reloads the page to re-authenticate, but only once per session to avoid an
 * infinite reload loop when the 401 is persistent rather than a transient
 * session expiry.
 */
const reloadForAuthOnce = (): void => {
  if (hasReloadedForAuth()) {
    return;
  }

  markReloadedForAuth();
  window.location.reload();
};

const axiosService: AxiosInstance = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor for adding the bearer token dynamically
axiosService.interceptors.request.use(
  (config) => {
    const { ECM_URL } = checkEnvironment();

    config.baseURL = ECM_URL;
    // Token is added from cookies dynamically for each request
    const token: string | undefined = cookies.get<string>('access_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor to handle 401 errors
axiosService.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // A successful response means the session is valid again — reset the guard
    // so a genuine future session-expiry is allowed to trigger a reload.
    clearReloadGuard();

    const { responseURL } = response.request as { responseURL?: string };

    if (responseURL) {
      try {
        // Info: Redirecting to login page when session expires
        if (responseURL.includes('/login/auth')) {
          reloadForAuthOnce();
        }
      } catch (e) {
        Logger.log('Redirecting error', e);
      }
    }

    return response;
  },
  (error: AxiosError) => {
    // If the error status is 401, reload to re-authenticate. Guarded so a
    // persistent 401 (e.g. an unprovisioned endpoint) cannot loop forever.
    if (error.response?.status === 401) {
      reloadForAuthOnce();
    }

    if (error.code === AxiosError.ECONNABORTED) {
      return Promise.reject(AXIOS_TIMEOUT_ERROR_MESSAGE);
    }

    return Promise.reject(error);
  }
);

export default axiosService;
